import { HiddenSection } from '../../data/Constants';
import {
  IConfigurationState,
  IConfigurationValue,
  IDetailPageRow,
  IDiffFeature,
  IImagesData,
  ISalesText,
  ISection,
} from '../../../types';
import {
  getValueById,
  hasAssignedSection,
  hasAssignedVariables,
} from '../../services/DetailsPageHelper';

/**
 * This function returns the data table rows from the response
 * @param {IConfigurationState} state configuration state
 * @param {ISection[]} sections section array
 * @param {IDetailPageRow} rows list of rows
 * @param {string[]} subModels  list of subModels
 * @param {boolean} isAddSection is Add section flag
 */
export function getSectionRows(
  state: IConfigurationState,
  sections: ISection[],
  rows: IDetailPageRow[],
  subModels: string[],
  isAddSection: boolean
): void {
  sections.forEach((section) => {
    if (
      section.name?.toLowerCase().includes(HiddenSection.Hidden) ||
      section.name?.includes(HiddenSection.Scope)
    ) {
      return;
    }
    const subModelId = section?.id?.split('.').pop()?.split('_')[0];
    let isSubModel = false;
    if (!subModelId?.includes('S')) {
      isSubModel = true;
      subModels.push(subModelId);
    }
    if (
      !isAddSection &&
      (hasAssignedSection(section.sections) || hasAssignedVariables(section.variables))
    ) {
      rows.push({
        id: rows.length,
        sectionName: section.name,
        featureFamilyCode: '',
        featureFamily: '',
        featureCode: '',
        feature: '',
        added: false,
        isSection: true,
        productId: subModelId,
        isProduct: isSubModel ?? false,
      });
    }

    section?.variables.forEach((variable) => {
      const hasValues = variable.values?.some((value) => value.assigned);
      if (hasValues) {
        const values = variable.values.filter(
          (value) => value.assigned && (variable.valueType !== 'Number' || value.value > 0)
        );

        const variableId = variable.id.split('.').pop();
        const varShortSalesText: ISalesText = getValueById(state.shortSalesText, variableId);
        values.forEach((value: IConfigurationValue) => {
          const valShortSalesText: ISalesText = getValueById(state.shortSalesText, value.value);

          rows.push({
            id: rows.length,
            featureFamilyCode: variableId,
            featureFamily: varShortSalesText ? varShortSalesText.text : variable.name,
            featureCode: value.value,
            feature: valShortSalesText ? valShortSalesText.text : value.name,
            added: false,
          });
        });
      }
    });

    if (section.sections) {
      getSectionRows(state, section.sections, rows, subModels, !isSubModel);
    }
  });
}

/**
 * This function create the rows from the diffrense api response
 * @param {IDiffFeature} features feature list
 * @param {number} previousCount length of existing rows
 * @returns {IDetailPageRow} return the rows
 */
export function addFeatures(features: IDiffFeature[], previousCount: number): IDetailPageRow[] {
  const rows: IDetailPageRow[] = [];
  let count = previousCount;

  //push the others section
  rows.push({
    id: count++,
    sectionName: 'Others',
    featureFamilyCode: '',
    featureFamily: '',
    featureCode: '',
    feature: '',
    added: false,
    isSection: true,
    productId: '',
    isProduct: true,
  });

  features.forEach(function (item: { options: any[] | null; code: any; value: any }) {
    if (item.options != null && item.options.length > 0) {
      item.options.forEach(function (option) {
        rows.push({
          id: count++,
          featureFamilyCode: item.code,
          featureFamily: item.value,
          featureCode: option.code,
          feature: option.value,
          added: true,
        });
      });
    } else {
      rows.push({
        id: count++,
        featureFamilyCode: item.code,
        featureFamily: item.value,
        featureCode: '',
        feature: '',
        added: true,
      });
    }
  });

  return rows;
}

/**
 * This function set the current  property associations   to the state
 * @param { string } images image response
 * @param { IConfigurationState } state configuration state
 * @returns { void }
 */
export function setProductImages(images: string, state: IConfigurationState): void {
  images = images && typeof images === 'string' ? JSON.parse(images) : images;

  const updateImages = new Map<string, IImagesData>(state.images?.get(state.productId));
  if (images) {
    images?.productImages?.forEach((image: IImagesData) => {
      if (image.materialCode === state.productId && !(image.materialCode in updateImages)) {
        updateImages.set(image.materialCode, image);
      }
    });
  }
  state.images?.set(state.productId, updateImages);
}

/**
 * This function updates the short salestext to the state
 * @param { [key: string]: { salesTexts: ISalesText[]; }; } action action data
 * @param {IConfigurationState} state configuration State
 */
export function updateShortSalesText(
  action: { salesTexts: { [key: string]: { salesTexts: ISalesText[] } } },
  state: IConfigurationState
): void {
  //Set the long Salestext  for all the  features to the  short SalesText Map  ex: <FeatureId,SalesText>
  state.shortSalesText.set(state.productId, new Map<string, ISalesText>());

  if (!action?.salesTexts) {
    return;
  }

  Object.keys(action?.salesTexts)?.forEach((key: string) => {
    //Initialize the short sales text data to empty map data
    const shortSalesText = new Map<string, ISalesText>();
    action?.salesTexts[key].salesTexts.forEach((text) => {
      if (text.id) {
        shortSalesText.set(text.id, text);
      }
    });
    //Set the short Salestext data for the Product to the state ex: <ProductId, <FeatureId,SalesText>>
    state.shortSalesText.set(key, shortSalesText);
  });
}
